/**
 * Supabase Realtime Service
 * Subscribes to user_data table changes and dispatches CustomEvents so
 * the rest of the app can react without polling.
 *
 * SQL you need to run in Supabase → SQL Editor (see docs below).
 */
import { supabase, isConfigured } from './supabase';

let channel = null;
let presenceChannel = null;

/**
 * Subscribe to real-time changes on the user_data table.
 * When another user saves data, the subscriber receives the event and
 * dispatches 'crm-realtime-update' with { key, userId, value }.
 */
export function subscribeToUserData(currentUserId, onUpdate) {
  if (!isConfigured || !supabase) return () => {};
  if (channel) channel.unsubscribe();

  channel = supabase
    .channel('crm-user-data')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'user_data' },
      payload => {
        const row = payload.new || payload.old;
        if (!row) return;
        onUpdate?.({ key: row.key, userId: row.user_id, value: row.value, updatedAt: row.updated_at, isSelfUpdate: row.user_id === currentUserId });
      }
    )
    .subscribe();

  return () => {
    if (channel) { channel.unsubscribe(); channel = null; }
  };
}

/** Online presence — see who else is active right now */
export function subscribePresence(currentUser, onPresenceChange) {
  if (!isConfigured || !supabase || !currentUser?.id) return () => {};
  if (presenceChannel) presenceChannel.unsubscribe();

  presenceChannel = supabase
    .channel('crm-presence', { config: { presence: { key: currentUser.id } } });

  presenceChannel
    .on('presence', { event: 'sync' }, () => {
      const state = presenceChannel.presenceState();
      const online = Object.values(state).flat().map(p => p);
      onPresenceChange?.(online);
    })
    .on('presence', { event: 'join' }, ({ key, newPresences }) => {
      onPresenceChange?.(null, { type: 'join', key, presences: newPresences });
    })
    .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
      onPresenceChange?.(null, { type: 'leave', key, presences: leftPresences });
    })
    .subscribe(async status => {
      if (status === 'SUBSCRIBED') {
        await presenceChannel.track({
          user_id: currentUser.id,
          name: currentUser.name || currentUser.email,
          avatar: currentUser.avatar || null,
          online_at: new Date().toISOString(),
        });
      }
    });

  return () => {
    if (presenceChannel) { presenceChannel.unsubscribe(); presenceChannel = null; }
  };
}

export function unsubscribeAll() {
  if (channel) { channel.unsubscribe(); channel = null; }
  if (presenceChannel) { presenceChannel.unsubscribe(); presenceChannel = null; }
}
